/**
 * 용량 룰 화면의 순수 도메인 유틸.
 * 부수효과·DOM 접근 없음 — 단위 테스트로 고정한다(계획 C-13 D19 b층).
 */
import { DateTime } from "luxon";
import {
  AdminDoseRuleNote,
  AdminDoseRuleVersionListItem,
  DoseRuleContent,
  DoseRuleSegment,
  DoseRuleStatus,
  SegmentKind,
  PatientCondition,
  LabelDailyDose,
  DroppedContraindication,
  DosePerUnit,
  DoseProductDispense,
  DoseProductIdentity,
  DoseProductRef,
  DoseTargetProduct,
} from "services/api/doseRules/types";

/**
 * 병합(broad) 시 값이 합쳐질 수 있는 용량 필드.
 * 이 필드들에서 raw 세그먼트 간 값이 갈리면 broad는 그 차이를 감춘다 —
 * 이 화면이 존재하는 이유(계획 §3.2).
 */
export const MERGE_DIFF_FIELDS = [
  "dosemin",
  "dosemax",
  "daymax",
  "doseminperkg",
  "dosemaxperkg",
  "daymaxperkg",
  "age_min_months",
  "age_max_months",
  "weight_min_kg",
  "weight_max_kg",
] as const;

/** 용량 필드 → 한국어 라벨. */
export const DOSE_FIELD_LABELS: Record<string, string> = {
  dosemin: "1회 최소",
  dosemax: "1회 최대",
  daymax: "1일 최대",
  doseminperkg: "1회 최소(/kg)",
  dosemaxperkg: "1회 최대(/kg)",
  daymaxperkg: "1일 최대(/kg)",
  age_min_months: "최소 월령",
  age_max_months: "최대 월령",
  weight_min_kg: "최소 체중(kg)",
  weight_max_kg: "최대 체중(kg)",
};

/** 상태값 → 한국어 라벨. */
export const DOSE_RULE_STATUS_LABELS: Record<string, string> = {
  pending_review: "검수 대기",
  active: "승인됨",
  archived: "반려/보관",
  gate_blocked: "차단",
};

export const doseRuleStatusLabel = (status: string | null | undefined): string => {
  if (!status) return "—";
  return DOSE_RULE_STATUS_LABELS[status] ?? status;
};

/** 구간 종류(segment_kind) → 한국어 라벨(D1·D14). 배지·설명에 쓴다. */
export const SEGMENT_KIND_LABELS: Record<SegmentKind, string> = {
  dose: "용량",
  contraindicated: "금기",
  not_established: "미확립",
  not_recommended: "권장안함",
};

/** 배지 톤: 금기 빨강, 미확립·권장안함 회색(화면 설계 표). `dose`는 배지 없음. */
export type SegmentBadgeTone = "red" | "gray" | "yellow" | "blue";
export const SEGMENT_KIND_TONE: Partial<Record<SegmentKind, SegmentBadgeTone>> = {
  contraindicated: "red",
  not_established: "gray",
  not_recommended: "gray",
};

/** 미확립·권장안함 구간 카드 본문에 보일 유형 문구(화면 설계). */
export const SEGMENT_KIND_BODY_PHRASE: Partial<Record<SegmentKind, string>> = {
  not_established: "안전성·유효성 미확립",
  not_recommended: "권장되지 않음(권장안함)",
};

/** 환자 상태 조건 종류(D4) → 한국어. */
export const patientConditionKindLabel = (
  kind: PatientCondition["kind"] | string | null | undefined
): string => {
  if (kind === "renal") return "신기능";
  if (kind === "hepatic") return "간기능";
  return "기타";
};

/** 제외한 금기 분류(D2) → 한국어. */
export const droppedReasonLabel = (
  reason: DroppedContraindication["reason"] | string | null | undefined
): string => {
  if (reason === "pregnancy") return "임부";
  if (reason === "lactation") return "수유부";
  if (reason === "disease") return "질환";
  return "기타";
};

/** 병합 판정에서 제외하는 경계 필드(월령·체중 4개). D14. */
const BOUNDARY_FIELDS: readonly string[] = [
  "age_min_months",
  "age_max_months",
  "weight_min_kg",
  "weight_max_kg",
];

/** 값 규칙(D1) 판정에 쓰는 6개 용량 필드. */
const DOSE_VALUE_FIELDS: readonly string[] = [
  "dosemin",
  "dosemax",
  "daymax",
  "doseminperkg",
  "dosemaxperkg",
  "daymaxperkg",
];

/**
 * 값 규칙(D1)으로 금기(`dosemax === 0`)·미확립(6필드 전부 null) 구간인지 판정한다.
 * **오직 "병합됨" 계산에서 구간을 뺄 때만 쓴다** — 배지 표시에는 쓰지 않는다
 * (D14: 옛 룰 구간 종류는 값으로 추정하지 않고 "옛 기준" 배지 하나로 대체).
 */
const isContraOrNotEstablishedByValue = (seg: DoseRuleSegment): boolean => {
  if (seg?.dosemax === 0) return true; // 금기
  return DOSE_VALUE_FIELDS.every(
    (f) => seg?.[f] === null || seg?.[f] === undefined
  ); // 미확립·권장안함(6필드 null)
};

/**
 * broad에서 "병합으로 값이 달라진" 용량 필드 키 집합을 계산한다(옛 룰 전용).
 *
 * 규칙(D14 개정): rawContent.segments 중 **금기·미확립 구간을 뺀** 나머지에서,
 * **경계 필드(월령·체중)를 뺀** 용량 필드의 distinct non-null 값이 2개 이상이면
 * broad에서 하나로 병합되며 원본 차이를 감춘다 → "병합됨"으로 표기.
 * - 금기(`dosemax 0`)와 일반값이 섞여 "병합됨"이 오탐하는 것을 막는다.
 * - 연령 레인이 여럿이라 경계가 갈리는 것만으로 늘 켜지던 오탐을 막는다.
 * - 새 기준 룰(criteria_version 있음)의 "병합됨"은 이 함수가 아니라 카드의
 *   `merged_indications`로 판정한다(호출부에서 옛 룰에만 이 함수를 쓴다).
 * rawContent가 없으면(전개본/구버전) 빈 집합. 모든 접근은 방어적.
 */
export const getMergedFieldKeys = (
  rawContent: DoseRuleContent | null | undefined
): Set<string> => {
  const result = new Set<string>();
  const segments = rawContent?.segments;
  if (!rawContent || !Array.isArray(segments) || segments.length === 0) {
    return result;
  }

  const comparable = segments.filter(
    (seg) => !isContraOrNotEstablishedByValue(seg)
  );

  for (const field of MERGE_DIFF_FIELDS) {
    if (BOUNDARY_FIELDS.includes(field)) continue;
    const distinct = new Set<unknown>();
    for (const seg of comparable) {
      const value = seg?.[field];
      if (value !== null && value !== undefined) {
        distinct.add(value);
      }
    }
    if (distinct.size >= 2) {
      result.add(field);
    }
  }
  return result;
};

/** 세그먼트 배열을 방어적으로 꺼낸다. */
export const getSegments = (
  content: DoseRuleContent | null | undefined
): DoseRuleSegment[] => {
  const segments = content?.segments;
  return Array.isArray(segments) ? segments : [];
};

/** 숫자/문자 값을 표시용 문자열로. null/undefined는 "—". */
export const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "—";
  }
  if (typeof value === "boolean") return value ? "예" : "아니오";
  return String(value);
};

/** ISO 문자열 → ko-KR 날짜시간. null/파싱실패는 "—"/원문. */
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** 세그먼트의 적응증/기본 여부를 한 줄 제목으로. */
export const segmentTitle = (seg: DoseRuleSegment, index: number): string => {
  const indication =
    typeof seg?.indication === "string" && seg.indication.trim()
      ? seg.indication.trim()
      : `세그먼트 ${index + 1}`;
  return seg?.is_default ? `${indication} (기본)` : indication;
};

/**
 * `gate_issues`(문자열 배열)를 방어적으로 사람이 읽을 문자열 배열로.
 * 룰 `provenance.gate_issues`(차단 사유)와 제품 `checks.gate_issues`(제품 검산 이슈)
 * 양쪽에서 같은 형태로 온다(D15). 예상 밖 형태(객체·원시값)도 방어한다.
 */
export const readGateIssues = (
  source: Record<string, unknown> | null | undefined
): string[] => {
  const raw = source?.gate_issues;
  if (!raw) return [];
  const toText = (v: unknown): string =>
    typeof v === "string"
      ? v
      : (() => {
          try {
            return JSON.stringify(v);
          } catch {
            return String(v);
          }
        })();
  if (Array.isArray(raw)) return raw.map(toText).filter(Boolean);
  if (typeof raw === "object") {
    return Object.entries(raw as Record<string, unknown>).map(
      ([k, v]) => `${k}: ${toText(v)}`
    );
  }
  return [toText(raw)];
};

/** min~max를 "lo~hi단위" / "lo단위 이상" / "hi단위 이하"로. 둘 다 없으면 null. */
const rangeText = (
  min: unknown,
  max: unknown,
  unit: string
): string | null => {
  const lo = typeof min === "number" && Number.isFinite(min) ? min : null;
  const hi = typeof max === "number" && Number.isFinite(max) ? max : null;
  if (lo === null && hi === null) return null;
  if (lo !== null && hi !== null) return `${lo}~${hi}${unit}`;
  if (lo !== null) return `${lo}${unit} 이상`;
  return `${hi}${unit} 이하`;
};

/**
 * 세그먼트의 연령·체중 경계를 한 줄로(D14 미확립·권장안함 카드, 금기 카드 표시용).
 * 예: "0~1개월", "3kg 이상", "0~1개월 · 3~10kg". 경계가 없으면 null.
 */
export const segmentBoundaryText = (seg: DoseRuleSegment): string | null => {
  const age = rangeText(seg?.age_min_months, seg?.age_max_months, "개월");
  const weight = rangeText(seg?.weight_min_kg, seg?.weight_max_kg, "kg");
  const parts = [age, weight].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(" · ") : null;
};

/**
 * 원문 1일량(D6)을 한 줄 문구로(화면 설계). 예: "5~60mg, 1~4회 분할".
 * 용량 범위가 없으면 null(줄을 그리지 않는다).
 */
export const formatLabelDailyDose = (
  ldd: LabelDailyDose | null | undefined
): string | null => {
  if (!ldd) return null;
  const unit = typeof ldd.unit === "string" ? ldd.unit : "";
  const amount = rangeText(ldd.amount_min, ldd.amount_max, unit);
  if (!amount) return null;
  const split = rangeText(ldd.split_min, ldd.split_max, "회");
  const perKg = ldd.per_kg ? "체중당 " : "";
  const splitText = split ? `, ${split} 분할` : "";
  return `${perKg}${amount}${splitText}`;
};

/* ── 단위·성분량 병기 (D-1), 월령·제외 사유·값 없음·기준 성분 복원 ──
 * 이 spec(2026-09-18)이 추가하는 순수 포맷·복원 유틸. 백엔드 응답에 이미 있는 값을
 * 화면에 결정적으로 그린다. DOM·부수효과 없음 — 단위 테스트로 고정한다.
 */

/** 값 없음 3종(D-5). "미제공"=서버 필드 없음 / "미기록"=옛 추출 / "해당 없음"=null이 의미. */
export const NOT_PROVIDED_TEXT = "미제공";
export const NOT_RECORDED_TEXT = "미기록";
export const NOT_APPLICABLE_TEXT = "해당 없음";

/** 처방단위가 없어 성분량 병기가 불가한 값 뒤에 붙이는 접미어(D-1). */
export const UNIT_UNKNOWN_SUFFIX = "(단위 미상)";
/** 처방단위가 없어 판정에서 빠지는 룰의 카드 상단 경고(D-1). */
export const UNIT_MISSING_WARNING = "처방단위가 없어 판정되지 않는 룰입니다";

/** 절대 용량 필드(처방단위 그대로). */
const DOSE_ABS_FIELDS: ReadonlySet<string> = new Set(["dosemin", "dosemax", "daymax"]);
/** 체중당(per-kg) 용량 필드("{처방단위}/kg"). */
const DOSE_PER_KG_FIELDS: ReadonlySet<string> = new Set([
  "doseminperkg",
  "dosemaxperkg",
  "daymaxperkg",
]);

/** 용량 값 필드(단위 병기 대상)인지. */
export const isDoseValueField = (key: string): boolean =>
  DOSE_ABS_FIELDS.has(key) || DOSE_PER_KG_FIELDS.has(key);

/**
 * 처방단위 출처 우선순위(D-1): `targetProduct.dispense.prescribe_unit` →
 * `productRef.prescribe_unit`. 둘 다 없으면 null.
 */
export const resolvePrescribeUnit = (
  targetProduct: DoseTargetProduct | null | undefined,
  productRef: DoseProductRef | null | undefined
): string | null => {
  const fromDispense = (targetProduct?.dispense as DoseProductDispense | null | undefined)
    ?.prescribe_unit;
  if (typeof fromDispense === "string" && fromDispense.trim()) return fromDispense.trim();
  const fromRef = productRef?.prescribe_unit;
  if (typeof fromRef === "string" && fromRef.trim()) return fromRef.trim();
  return null;
};

/** 단위로 유효한 문자열인가 — 빈 값과 리터럴 "null"을 제외한다(09-25 결정 4 경계 ③). */
const isValidVersionUnit = (v: unknown): v is string =>
  typeof v === "string" && v.trim() !== "" && v.trim() !== "null";

/**
 * 과거 버전 처방단위(09-25 결정 4 — @spec:2026-09-29-처방검토-판정기록-조회화면 소유분).
 * 그 버전 `productRef.prescribe_unit`이 유효하면 그 값, 없으면 `targetProduct` 폴백, 둘 다
 * 없으면 null. **공용 `resolvePrescribeUnit`은 쓰지 않는다** — 그쪽은 리터럴 문자열 `"null"`을
 * 유효 단위로 통과시켜 `"12null"`을 렌더한다(현황 6). 여기선 `"null"`을 제외해 null → `(단위 미상)`.
 * 판정 기록 viewer는 `targetProduct`를 넘기지 않아(versions API가 싣지 않는다) 폴백 없이 null이다.
 */
export const resolveVersionPrescribeUnit = (
  productRef: DoseProductRef | null | undefined,
  targetProduct?: DoseTargetProduct | null
): string | null => {
  const fromRef = productRef?.prescribe_unit;
  if (isValidVersionUnit(fromRef)) return fromRef.trim();
  const fromDispense = (targetProduct?.dispense as DoseProductDispense | null | undefined)
    ?.prescribe_unit;
  if (isValidVersionUnit(fromDispense)) return fromDispense.trim();
  return null;
};

/** 유효숫자 `sig`자리로 반올림 + 천단위 구분(성분량 병기용, D-1). */
export const formatSignificant = (n: number, sig = 3): string => {
  if (!Number.isFinite(n)) return String(n);
  if (n === 0) return "0";
  const digits = Math.ceil(Math.log10(Math.abs(n)));
  const power = sig - digits;
  const factor = Math.pow(10, power);
  const rounded = Math.round(n * factor) / factor;
  return rounded.toLocaleString("en-US", {
    maximumFractionDigits: Math.max(0, power),
  });
};

/** 단위 문맥(D-1) — 처방단위 + 1단위 함량(per_unit). */
export interface DoseUnitContext {
  prescribeUnit: string | null | undefined;
  perUnit?: DosePerUnit | null;
}

/**
 * 용량 값에 처방단위(+성분량 mg 병기)를 붙인다(D-1).
 * - 절대 용량: "12정", 체중당: "0.5정/kg"
 * - `per_unit`이 `mg/{처방단위}` 꼴이면 괄호로 성분량 병기: "12정 (60mg)" · "0.5정/kg (5mg/kg)"
 *   (곱셈 결과는 유효숫자 3자리)
 * - 처방단위가 없으면 "2 (단위 미상)"
 */
export const formatDoseValueField = (
  fieldKey: string,
  value: number,
  ctx: DoseUnitContext
): string => {
  const isPerKg = DOSE_PER_KG_FIELDS.has(fieldKey);
  const unit =
    typeof ctx.prescribeUnit === "string" && ctx.prescribeUnit.trim()
      ? ctx.prescribeUnit.trim()
      : null;
  if (!unit) return `${value} ${UNIT_UNKNOWN_SUFFIX}`;
  let out = `${value}${unit}${isPerKg ? "/kg" : ""}`;
  const perUnit = ctx.perUnit;
  if (
    perUnit &&
    typeof perUnit.value === "number" &&
    Number.isFinite(perUnit.value) &&
    perUnit.unit === `mg/${unit}`
  ) {
    const mg = value * perUnit.value;
    out += ` (${formatSignificant(mg)}mg${isPerKg ? "/kg" : ""})`;
  }
  return out;
};

/**
 * 월령을 "N개월 (X세[· Y개월])"로(D-1). 12의 배수면 "(N/12세)", 아니면 "(X세 Y개월)".
 * 예: 216 → "216개월 (18세)", 30 → "30개월 (2세 6개월)".
 */
export const formatMonthAge = (months: number): string => {
  if (!Number.isFinite(months)) return String(months);
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yearText = rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
  return `${months}개월 (${yearText})`;
};

/** 판정 대상 여부(dose_check_eligible) → 한글(D-2 row6). */
export const doseCheckEligibleLabel = (v: string | null | undefined): string => {
  if (v === "yes") return "판정 대상";
  if (v === "no") return "판정 대상 아님";
  if (v === "uncertain") return "불확실";
  return NOT_PROVIDED_TEXT;
};

/** 판정 제외 사유(dose_check_ineligible_reason) → 한글(D-2 row6). 미지의 값은 원문. */
export const DOSE_INELIGIBLE_REASON_LABELS: Record<string, string> = {
  topical_free_dose: "외용제(자유 용량)",
  unit_mismatch: "단위 불일치",
  not_ingested: "비경구(비복용)",
  bsa_based: "체표면적 기준",
  other: "기타",
};
export const doseIneligibleReasonLabel = (reason: string | null | undefined): string => {
  if (!reason) return "";
  return DOSE_INELIGIBLE_REASON_LABELS[reason] ?? reason;
};

/** 1단위 함량 기준 성분·염(D-7). */
export interface BasisIngredientInfo {
  basis: string | null;
  salt: string | null;
}

/**
 * 1단위 함량의 기준 성분·염을 저장된 `identity`에서 결정적으로 복원한다(D-7).
 * 백엔드 stamp와 같은 규칙: `ingredients[0].strengths.find(is_ud_basis) ?? strengths[0]`의
 * `basis`, 염은 `ingredients[0].salt`. 저장하지 않고 정본 규칙을 재적용한다(옛 행에도 동일).
 */
export const restoreBasisIngredient = (
  identity: DoseProductIdentity | null | undefined
): BasisIngredientInfo => {
  const first = identity?.ingredients?.[0];
  const strengths = Array.isArray(first?.strengths) ? first?.strengths ?? [] : [];
  const basisRow = strengths.find((st) => st?.is_ud_basis) ?? strengths[0];
  const basis =
    typeof basisRow?.basis === "string" && basisRow.basis.trim() ? basisRow.basis.trim() : null;
  const salt = typeof first?.salt === "string" && first.salt.trim() ? first.salt.trim() : null;
  return { basis, salt };
};

/**
 * archived를 뺀 전개본 수(D-4). 승인 확인창의 동반 전이 수 =
 * 차단 룰 승인 경로(반려 제외)와 같은 계산. 서버 실제 동반 전이 수와 맞춘다.
 */
export const countActiveDerived = (
  derived: Array<{ status?: string | null }> | null | undefined
): number => (derived ?? []).filter((d) => d?.status !== "archived").length;

/** 목록 필터에서 고를 수 있는 상태(전이와 달리 pending_review·gate_blocked 포함). */
export const FILTERABLE_STATUSES: Array<{ value: DoseRuleStatus | "all"; label: string }> = [
  { value: "pending_review", label: "검수 대기" },
  { value: "active", label: "승인됨" },
  { value: "archived", label: "반려/보관" },
  { value: "gate_blocked", label: "차단" },
  { value: "all", label: "전체" },
];

/* ── 룰 버전 절 (Phase 1, spec `2026-09-25-처방검토-관리자룰화면-룰버전-UI`) ──
 *
 * 여기 있는 것은 전부 순수 함수·상수다(DOM·부수효과 없음). 컴포넌트는 결과 문자열을 그리기만
 * 한다 — 화이트리스트·`×N`·` · ` 연결을 DOM 없이 단정할 수 있고, Phase 2(과거 버전 본문 열람)가
 * 같은 선별을 재사용한다(계획 F-3).
 */

/** 절의 노출 문구. 테스트가 리터럴을 중복 소유하지 않게 export 한다(계획 B-1). */
export const VERSION_SECTION_TITLE = "룰 버전";
export const VERSION_EMPTY_TEXT = "이력 없음";
export const VERSION_CURRENT_TEXT = "현재";
export const VERSION_NOTE_EMPTY_TEXT = "메모 없음";
export const VERSION_RETIRED_SUFFIX = "(은퇴)";
export const BACKFILL_REASON_TEXT = "백필로 복원된 과거 버전";
export const VERSION_LOAD_ERROR_TEXT = "룰 버전을 불러오지 못했습니다";
export const NOTES_LOAD_ERROR_TEXT = "은퇴 사유를 불러오지 못했습니다";
export const RETRY_TEXT = "다시 시도";

/**
 * 버전 줄 **윗줄** 표 헤더 4칸(보정 7a). 메모·사유는 전폭 보조 줄로 내려갔으므로 헤더 칸이 없다.
 * 보조 줄 셀의 `colSpan`은 이 배열 길이에서 파생시킨다(P-4) — 두 번째 진실을 만들지 않는다.
 *
 * **왜 6칸에서 4칸이 됐나(보정 7a)** — 은퇴 시각은 `formatDateTime` 전문(`2026. 09. 22. 오전 11:39`)을
 * 자르면 안 되고(설계 결정 15) 그 칸만 약 170px를 먹는다. 1280px 뷰포트에서 좌측 패널(기본 46%)이
 * 주는 폭으로 6칸을 나누면 가변 3칸이 각 45px 남짓이 되어 **메모·사유가 한글 2~3자만 보였다**
 * (57b2d6189 스크린샷: 메모 `소아 1…` · 사유 `재추…`). 칸 안에서는 산술적으로 풀리지 않는다.
 */
export const VERSION_COLUMNS = ["버전", "상태", "은퇴", "검수자"] as const;

/**
 * 보조 줄의 라벨(보정 7a). 헤더 칸이 사라진 자리를 줄 안의 라벨이 대신한다.
 * 인접 `검수 메모` 블록과 라벨이 충돌하지 않게 메모 쪽은 `메모`만 쓴다(Phase 1 편차 표와 같은 이유).
 */
export const VERSION_NOTE_LABEL = "메모";
export const VERSION_REASON_LABEL = "은퇴 사유";

/**
 * 사유 칸이 **조회 진행 중**일 때의 표기(보정 7d). 빈 칸(사유 없음)·`—`(조회 실패)와 셋이 모양으로
 * 구분된다. 비활성(idle)에는 쓰지 않는다 — 버전 1개 룰은 `notes`를 아예 요청하지 않는데 그때
 * `…`가 뜨면 "불러오는 중"이라는 거짓말이 된다.
 */
export const NOTES_LOADING_TEXT = "…";

/**
 * 버전이 2개 이상인 룰의 현재 버전에 메모가 없을 때 검수 메모 블록이 쓰는 문구(보정 7f · 설계 결정 13).
 * 블록을 감추면 "또 지워졌다"와 "원래 안 썼다"가 구분되지 않는다 — 이 작업이 없애려는 바로 그 체감이다.
 */
export const VERSION_NO_MEMO_TEXT = "이 버전에 남긴 메모가 없습니다";

/**
 * `×N` 배지의 `title`(보정 7e). 지금까지는 title에도 `×N`과 같은 문자열뿐이라
 * "N번 은퇴했다"로 읽혔다 — 실제 뜻은 "같은 사유가 N번 접혔다"다.
 */
export const retireRepeatTitle = (repeatCount: number): string =>
  `같은 사유로 ${repeatCount}번 은퇴 — 이후 버전의 사유가 여기로 합쳐짐`;

/**
 * 은퇴 사유로 인정하는 노트 author — **화이트리스트**다(계획 E-1).
 * `system:`으로 넓히면 배제 대상인 전개본 동반 전이 노트가 섞인다. `kind=system` 생산자는 넷이고
 * 은퇴와 관계있는 것은 둘(재추출·백필)뿐이다. gate 승인 접두 노트의 author는 관리자 id라
 * 자동으로 빠진다 — 사람 id가 사유 칸에 노출되는 경로가 없다.
 */
export const REEXTRACT_NOTE_AUTHOR = "system:reextract";
export const BACKFILL_NOTE_AUTHOR_PREFIX = "system:backfill-";

/**
 * 메타 그리드의 `룰 버전` 값(계획 A-2). 세 갈래 — `undefined`가 화면에 닿는 경로를 남기지 않는다.
 * - `version` 없음 → `—`
 * - `version`만 있음 → `vN` (빈 괄호를 만들지 않는다)
 * - 둘 다 있음 → `vN (M개)`
 */
export const formatRuleVersionMeta = (version?: number, versionCount?: number): string => {
  if (typeof version !== "number" || !Number.isFinite(version)) return "—";
  if (typeof versionCount !== "number" || !Number.isFinite(versionCount)) return `v${version}`;
  return `v${version} (${versionCount}개)`;
};

/** 버전 칸·목록 배지의 `vN`. 숫자가 아니면 `—`(계획 X-2 — `vundefined`를 만들지 않는다). */
export const formatVersionNumber = (version?: number): string =>
  typeof version === "number" && Number.isFinite(version) ? `v${version}` : "—";

/**
 * 현재 버전 줄인지 — **`retiredAt`이 빈 값인 줄**이다(계획 §3-1).
 * `uk_dose_rules_current(rule_id, is_current)`가 DB 차원에서 보장하는 것은 **최대 하나**다.
 * "정확히 하나"는 은퇴와 출생을 한 트랜잭션에 묶은 코드가 보장한다(마이그 v1.0.76 :34-41) —
 * UNIQUE 제약이 0개인 경우를 막지는 못한다(보정 7h 주석 정정). 불변식이 깨져 0개·2개가 와도
 * **클라이언트가 중재하지 않는다**(C-5) — 중재하면 백엔드 버그를 화면이 감춘다.
 * `==`를 쓰지 않는 이유는 `eqeqeq` 경고다(A-13).
 */
export const isCurrentVersionRow = (
  row: AdminDoseRuleVersionListItem | null | undefined
): boolean => {
  const retiredAt = row?.retiredAt;
  return retiredAt === null || retiredAt === undefined || retiredAt === "";
};

/**
 * 버전 줄의 상태 문구. 은퇴는 `deleted_at`만 박고 `status`를 건드리지 않으므로, 상태를 그대로
 * 재사용하면 `active`로 은퇴한 행이 "승인됨"으로 뜬다(dev 실측 1건). 그래서 접미어를 붙인다.
 * - 현재 줄 → 라벨 그대로 (`승인됨`)
 * - 은퇴 줄 → `승인됨(은퇴)`
 * - `status` 없음 → **`—`만**(계획 X-1). `—(은퇴)`는 "값 없음" 기호를 오염시키고, 은퇴 사실은
 *   `은퇴` 칸이 이미 전달한다.
 */
export const versionStatusLabel = (status?: string, isCurrent?: boolean): string => {
  const label = doseRuleStatusLabel(status);
  if (isCurrent || label === "—") return label;
  return `${label}${VERSION_RETIRED_SUFFIX}`;
};

/**
 * 한 건의 은퇴 사유. **`×N`을 문자열에 섞지 않는다**(보정 7e) — 화면이 `×N`에 별도 `title`을
 * 달아야 하므로 요소를 나눌 수 있게 수치를 그대로 들고 다닌다.
 */
export interface RetireReasonEntry {
  /** 노트 `body`(재추출) 또는 백필 고정 문구. `×N`은 포함하지 않는다. */
  text: string;
  /** 서버가 같은 body를 접은 횟수. 2 이상일 때만 화면에 `×N`이 붙는다. */
  repeatCount?: number;
}

/**
 * `ruleRowId → 은퇴 사유[]` Map을 한 번 만든다(계획 H-1).
 *
 * 줄마다 `notes.filter(...)`를 돌리면 버전 V개 × 노트 N개가 되고 `/notes`엔 LIMIT·페이징이
 * 없어 N의 서버 상한이 없다. 구성 O(N)·조회 O(1)로 내린다.
 *
 * 규칙:
 * - 배열이 아니면 빈 Map (I-3 — 이 화면 위에 에러 바운더리가 없어 렌더가 throw하면 상세가 백지다)
 * - `ruleRowId`가 숫자가 아닌 노트는 넣지 않는다 (W-2 — optional 필드라 `undefined === undefined`가
 *   true가 되어 **모든 노트가 모든 줄에 붙는** 사고를 막는다)
 * - **`kind === "system"`이 아니면 제외** (설계 결정 5 판정식). 버전 도입 전 러너가 system 값을
 *   `reviewed_by`·`review_note` **컬럼**에 썼으므로 백필이 `kind=review` + `author=system:…`
 *   노트를 만들 수 있다 — author만 보면 `system:backfill-*`가 사람 메모를 밀어내고 사유 칸에
 *   `백필로 복원된 과거 버전`을 띄운다. `kind` 조건이 그것을 거른다
 * - author 화이트리스트 밖이면 제외 (E-1). `system:reextract`는 **정확 일치**다 — `startsWith`로
 *   구현하면 전량 러너의 `system:reextract-2026-09-14`가 은퇴 사유로 새어 나온다
 * - 백필은 문구를 상수로 갈아치우므로 `body`를 요구하지 않는다. 재추출은 `body`가 곧 문구라
 *   빈 `body`면 버린다 (Z-1·E-3)
 */
export const buildRetireReasonMap = (
  notes: AdminDoseRuleNote[] | null | undefined
): Map<number, RetireReasonEntry[]> => {
  const map = new Map<number, RetireReasonEntry[]>();
  if (!Array.isArray(notes)) return map;

  for (const note of notes) {
    const rowId = note?.ruleRowId;
    if (typeof rowId !== "number" || !Number.isFinite(rowId)) continue;

    if (note?.kind !== "system") continue;

    const author = typeof note?.author === "string" ? note.author : "";
    const isBackfill = author.startsWith(BACKFILL_NOTE_AUTHOR_PREFIX);
    const isReextract = author === REEXTRACT_NOTE_AUTHOR;
    if (!isBackfill && !isReextract) continue;

    const body = typeof note?.body === "string" ? note.body.trim() : "";
    if (!isBackfill && !body) continue;

    const repeat = note?.repeatCount;
    const entry: RetireReasonEntry = {
      text: isBackfill ? BACKFILL_REASON_TEXT : body,
      repeatCount:
        typeof repeat === "number" && Number.isFinite(repeat) ? repeat : undefined,
    };

    const existing = map.get(rowId);
    if (existing) existing.push(entry);
    else map.set(rowId, [entry]);
  }
  return map;
};

/**
 * 그 버전 줄의 은퇴 사유 목록. 매칭된 노트가 여럿이면 **전부** 돌려준다(계획 A-3) —
 * 하나를 골라 버리면 이 작업이 막으려는 "지워졌다"를 재생산한다.
 * 없으면 빈 배열이다(`—`가 아니다 — `—`는 조회 실패 전용 기호다).
 */
export const retireReasonEntries = (
  reasonMap: Map<number, RetireReasonEntry[]>,
  rowId?: number
): RetireReasonEntry[] => {
  if (typeof rowId !== "number" || !Number.isFinite(rowId)) return [];
  return reasonMap.get(rowId) ?? [];
};

/**
 * 같은 사유를 **평문 한 줄**로 이은 것. 칸이 2줄로 잘려도 `title`에는 전문이 실려야 하므로
 * 화면 요소(`×N` 배지)와 별개로 이 문자열이 필요하다. 없으면 빈 문자열이다.
 */
export const retireReasonText = (
  reasonMap: Map<number, RetireReasonEntry[]>,
  rowId?: number
): string =>
  retireReasonEntries(reasonMap, rowId)
    .map((entry) =>
      typeof entry.repeatCount === "number" && entry.repeatCount > 1
        ? `${entry.text} ×${entry.repeatCount}`
        : entry.text
    )
    .join(" · ");

/**
 * 판정 기록발 룰 메모 본문 접두 판별(D-41·D-39) — `판정 기록 #<logId> · <index+1>번째 약: `로
 * 시작하면 {logId, lineNo}, 아니면 null. 가운뎃점은 U+00B7(백엔드 생성·spec과 같은 문자, §6.2 7단계).
 */
export const parseDoseReviewLogNoteRef = (
  body: string | null | undefined
): { logId: number; lineNo: number } | null => {
  if (!body) return null;
  const m = /^판정 기록 #(\d+) · (\d+)번째 약: /.exec(body);
  if (!m) return null;
  return { logId: Number(m[1]), lineNo: Number(m[2]) };
};

/**
 * ISO(UTC Z) → +09:00 `YYYY-MM-DD HH:mm`(브라우저 TZ 무관 — 선례 DesktopDownload, D-41). 무효면 "시각 미상".
 */
export const formatKstMinute = (iso: string | null | undefined): string => {
  if (!iso) return "시각 미상";
  const dt = DateTime.fromISO(iso, { setZone: true }).setZone("Asia/Seoul");
  return dt.isValid ? dt.toFormat("yyyy-MM-dd HH:mm") : "시각 미상";
};
